import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { PRELIMINARY_STAGE } from "@legacy-manager/engine";

import "./chave.css";
import type { CupStageView, CupTieView } from "../game/types";
import { Crest } from "./atoms";
import { rotuloDeFase, useT } from "../i18n";

/**
 * A chave da copa: as duas metades crescendo até a final, no meio.
 *
 * Existe porque a lista de confrontos por fase responde "quem jogou contra
 * quem" e não responde "quem eu encontro se passar", que é a pergunta que
 * alguém faz olhando uma copa. A árvore só pôde ser desenhada depois que o
 * motor passou a sortear a chave uma vez só: enquanto cada fase resorteava os
 * sobreviventes, o caminho até a final não existia para ser mostrado.
 *
 * **Quantas fases cabem é a largura que decide.** Uma copa de 64 clubes tem
 * onze colunas, e onze colunas não entram na coluna do meio do hub sem virar
 * rolagem lateral. Então o componente mede o espaço que tem e mostra as fases
 * finais que couberem, das quartas para dentro no computador comum e mais
 * fases em tela larga. As que sobraram continuam na lista abaixo, que não
 * perdeu nada.
 */

/** Largura mínima de uma coluna para o nome do clube ainda dizer algo. */
const COLUNA_MINIMA = 118;

/** O vão entre colunas, onde os traços da chave são desenhados. Igual ao CSS. */
const VAO = 18;

/** Quantas fases finais desenhar, no máximo, mesmo sobrando largura. */
const FASES_NO_MAXIMO = 6;

interface Coluna {
  chaveDaFase: string;
  lado: "esq" | "dir" | "centro";
  /** `null` é confronto que ainda não foi sorteado. */
  confrontos: Array<CupTieView | null>;
  /** Alimenta a coluna seguinte, e por isso desenha o traço de saída. */
  liga: boolean;
  /** Recebe o traço da coluna anterior. */
  recebe: boolean;
}

/**
 * Quantas fases finais cabem na largura medida.
 *
 * Uma chave de `k` fases ocupa `2k - 1` colunas, porque só a final não se
 * repete nos dois lados.
 */
function fasesQueCabem(largura: number, disponiveis: number): number {
  for (let k = Math.min(disponiveis, FASES_NO_MAXIMO); k >= 2; k -= 1) {
    const colunas = k * 2 - 1;
    if (colunas * COLUNA_MINIMA + (colunas - 1) * VAO <= largura) return k;
  }
  return 0;
}

/**
 * As `k` fases finais que formam uma árvore de verdade.
 *
 * Anda de trás para frente e para na primeira fase que não tem o número de
 * confrontos da sua posição. É assim que a preliminar fica de fora: ela existe
 * justamente porque a copa não fechava redonda, e enfiá-la na árvore daria uma
 * coluna de caixas vazias que nunca vão ser preenchidas.
 */
function fasesFinais(stages: CupStageView[], k: number): CupStageView[] {
  const escolhidas: CupStageView[] = [];
  for (let i = 0; i < k; i += 1) {
    const fase = stages[stages.length - 1 - i];
    if (!fase || fase.key === PRELIMINARY_STAGE) break;
    if (fase.ties.length > 0 && fase.ties.length !== 2 ** i) break;
    escolhidas.unshift(fase);
  }
  return escolhidas.length >= 2 ? escolhidas : [];
}

/** A largura que o componente tem, acompanhada enquanto a janela muda. */
function useLargura(): [RefObject<HTMLDivElement | null>, number] {
  const alvo = useRef<HTMLDivElement | null>(null);
  const [largura, definir] = useState(0);

  useEffect(() => {
    const no = alvo.current;
    if (!no || typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver((entradas) => {
      const entrada = entradas[0];
      if (entrada) definir(entrada.contentRect.width);
    });
    observador.observe(no);
    return () => observador.disconnect();
  }, []);

  return [alvo, largura];
}

/**
 * Monta as colunas da chave a partir das fases finais.
 *
 * A metade de cima dos confrontos de uma fase é a metade da esquerda da
 * árvore, e a de baixo é a da direita. Isso vale em todas as fases porque o
 * motor guarda os vencedores na ordem dos confrontos, então a metade da
 * esquerda de uma fase só pode ter vindo da metade da esquerda da anterior.
 */
function montarColunas(chave: CupStageView[]): Coluna[] {
  const esquerda: Coluna[] = [];
  const direita: Coluna[] = [];
  let centro: Coluna | null = null;

  chave.forEach((fase, posicao) => {
    const daPonta = chave.length - 1 - posicao;
    const total = 2 ** daPonta;

    /* Fase ainda não sorteada entra como caixa vazia: a chave precisa mostrar
       a forma inteira, senão ela cresce a cada rodada e nunca se lê como uma. */
    const confrontos: Array<CupTieView | null> = Array.from(
      { length: total },
      (_, i) => fase.ties[i] ?? null,
    );

    if (total === 1) {
      centro = {
        chaveDaFase: fase.key,
        lado: "centro",
        confrontos,
        liga: false,
        recebe: posicao > 0,
      };
      return;
    }

    const meio = total / 2;
    esquerda.push({
      chaveDaFase: fase.key,
      lado: "esq",
      confrontos: confrontos.slice(0, meio),
      liga: true,
      recebe: posicao > 0,
    });
    direita.push({
      chaveDaFase: fase.key,
      lado: "dir",
      confrontos: confrontos.slice(meio),
      liga: true,
      recebe: posicao > 0,
    });
  });

  return [...esquerda, ...(centro ? [centro] : []), ...direita.reverse()];
}

export function ChaveDaCopa({
  stages,
  myClubId,
  aindaEmLista,
}: {
  stages: CupStageView[];
  myClubId: string;
  /**
   * Desenha as fases que não couberam na árvore.
   *
   * Quem mede a largura é este componente, então é ele que sabe o que sobrou.
   * Devolver a conta para quem chama daria duas contas em dois arquivos, e elas
   * discordariam no dia em que uma mudasse.
   */
  aindaEmLista: (fases: CupStageView[]) => ReactNode;
}): ReactNode {
  const [alvo, largura] = useLargura();

  const cabem = largura > 0 ? fasesQueCabem(largura, stages.length) : 0;
  const chave = cabem > 0 ? fasesFinais(stages, cabem) : [];
  const naChave = new Set(chave.map((fase) => fase.key));
  const colunas = montarColunas(chave);
  const sobraram = stages.filter((fase) => !naChave.has(fase.key) && fase.ties.length > 0);

  return (
    <div ref={alvo} className="grid" style={{ gap: 18 }}>
      {colunas.length > 0 && (
        <div className="chave">
          {colunas.map((coluna, indice) => (
            <ColunaDaChave
              key={`${coluna.chaveDaFase}-${coluna.lado}-${indice}`}
              coluna={coluna}
              myClubId={myClubId}
            />
          ))}
        </div>
      )}
      {aindaEmLista(sobraram)}
    </div>
  );
}

function ColunaDaChave({ coluna, myClubId }: { coluna: Coluna; myClubId: string }): ReactNode {
  const daEsquerda = coluna.lado === "dir" ? "dir" : "esq";

  return (
    <div className="chave-coluna" data-lado={coluna.lado}>
      <span className="label-muted chave-fase">{rotuloDeFase(coluna.chaveDaFase)}</span>
      <div className="chave-celulas">
        {coluna.confrontos.map((confronto, i) => (
          <div
            key={confronto ? `${confronto.homeId}-${confronto.awayId}` : `vazio-${i}`}
            className="chave-cel"
            {...(coluna.liga ? { "data-liga": "" } : {})}
            {...(i % 2 === 0 ? { "data-topo": "" } : {})}
          >
            {coluna.recebe && <span className="chave-entrada" data-de={daEsquerda} aria-hidden />}
            {/* A final recebe dos dois lados, e por isso ganha o segundo traço. */}
            {coluna.recebe && coluna.lado === "centro" && (
              <span className="chave-entrada" data-de="dir" aria-hidden />
            )}
            <CaixaDoConfronto confronto={confronto} myClubId={myClubId} />
          </div>
        ))}
      </div>
      {coluna.lado === "centro" && <Taca />}
    </div>
  );
}

function CaixaDoConfronto({
  confronto,
  myClubId,
}: {
  confronto: CupTieView | null;
  myClubId: string;
}): ReactNode {
  const t = useT();

  if (!confronto) {
    return (
      <div className="chave-caixa">
        <span className="chave-lado" data-caiu>
          <span className="chave-nome">{t("competicoes.aDefinir")}</span>
        </span>
        <span className="chave-lado" data-caiu>
          <span className="chave-nome">{t("competicoes.aDefinir")}</span>
        </span>
      </div>
    );
  }

  const jogado = confronto.legs.some((perna) => perna.played);
  const decidido = confronto.winnerId !== null;

  const lado = (
    id: string,
    nome: string,
    sigla: string,
    cores: [string, string],
    gols: number,
  ): ReactNode => {
    const passou = decidido && confronto.winnerId === id;
    return (
      <span
        className="chave-lado"
        {...(passou ? { "data-passou": "" } : {})}
        {...(decidido && !passou ? { "data-caiu": "" } : {})}
      >
        <Crest colors={cores} short={sigla} size={16} />
        <span className="chave-nome" title={nome}>
          {nome}
        </span>
        <span className="chave-gols num">{jogado ? gols : t("comum.vazio")}</span>
      </span>
    );
  };

  return (
    <div className="chave-caixa" {...(confronto.homeId === myClubId || confronto.awayId === myClubId ? { "data-minha": "" } : {})}>
      {lado(
        confronto.homeId,
        confronto.homeName,
        confronto.homeShort,
        confronto.homeColors,
        confronto.aggHome,
      )}
      {lado(
        confronto.awayId,
        confronto.awayName,
        confronto.awayShort,
        confronto.awayColors,
        confronto.aggAway,
      )}
    </div>
  );
}

/** A taça atrás da final, como no conceito. Decoração, e nada mais. */
function Taca(): ReactNode {
  return (
    <div className="chave-taca" aria-hidden>
      <svg width="34" height="40" viewBox="0 0 24 28" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7 2h10v7a5 5 0 0 1-10 0Z" />
        <path d="M7 4H4v2a4 4 0 0 0 3 3.8M17 4h3v2a4 4 0 0 1-3 3.8" />
        <path d="M12 14v5M8.5 22h7l1 4h-9Z" />
      </svg>
    </div>
  );
}
